import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Calendar,
  Badge,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Switch,
  InputNumber,
  List,
  Tag,
  Spin,
  message,
  Empty,
  Popconfirm,
  Space,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  BellOutlined,
  CrownOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import './PetCalendarPage.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EVENT_TYPES = [
  { value: 'VACCINE', label: 'Vaccine', color: 'blue' },
  { value: 'GROOMING', label: 'Grooming', color: 'purple' },
  { value: 'VET_VISIT', label: 'Vet Visit', color: 'red' },
  { value: 'MEDICATION', label: 'Medication', color: 'orange' },
  { value: 'FEEDING', label: 'Feeding', color: 'green' },
  { value: 'EXERCISE', label: 'Exercise', color: 'cyan' },
  { value: 'BIRTHDAY', label: 'Birthday', color: 'magenta' },
  { value: 'OTHER', label: 'Other', color: 'default' }
];

const RECURRENCE_TYPES = [
  { value: 'NONE', label: 'Does not repeat' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' }
];

const PetCalendarPage = observer(() => {
  const { t } = useTranslation();
  const { petStore, authStore } = useStores();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  useEffect(() => {
    if (authStore.isAuthenticated) {
      petStore.fetchPremiumStatus();
      petStore.fetchMyPets();
    }
  }, [authStore.isAuthenticated]);

  useEffect(() => {
    if (petStore.premiumStatus?.is_premium) {
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;
      petStore.fetchCalendarMonth(year, month);
      petStore.fetchUpcomingEvents();
    }
  }, [petStore.premiumStatus?.is_premium, selectedDate]);

  const handleMonthChange = (date) => {
    setSelectedDate(date);
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({
      event_date: selectedDate,
      reminder_enabled: true,
      reminder_days_before: 1,
      recurrence: 'NONE'
    });
    setModalVisible(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    form.setFieldsValue({
      ...event,
      event_date: dayjs(event.event_date),
      event_time: event.event_time ? dayjs(event.event_time, 'HH:mm:ss') : null,
      recurrence_end_date: event.recurrence_end_date ? dayjs(event.recurrence_end_date) : null
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    const eventData = {
      ...values,
      event_date: values.event_date.format('YYYY-MM-DD'),
      event_time: values.event_time ? values.event_time.format('HH:mm:ss') : null,
      recurrence_end_date: values.recurrence_end_date ? values.recurrence_end_date.format('YYYY-MM-DD') : null
    };

    let result;
    if (editingEvent) {
      result = await petStore.updateEvent(editingEvent.id, eventData);
    } else {
      result = await petStore.createEvent(eventData);
    }

    if (result.success) {
      message.success(editingEvent ? t('petCalendar.messages.eventUpdated') : t('petCalendar.messages.eventCreated'));
      setModalVisible(false);
      form.resetFields();
    } else {
      message.error(typeof result.error === 'object' ? JSON.stringify(result.error) : result.error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    const result = await petStore.deleteEvent(eventId);
    if (result.success) {
      message.success(t('petCalendar.messages.eventDeleted'));
    } else {
      message.error(result.error);
    }
  };

  const handleCompleteEvent = async (eventId) => {
    const result = await petStore.completeEvent(eventId);
    if (result.success) {
      message.success(result.data.message);
    } else {
      message.error(result.error);
    }
  };

  const getEventTypeInfo = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1];
  };

  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const dayEvents = petStore.calendarEvents.filter(
      event => event.event_date === dateStr
    );

    if (dayEvents.length === 0) return null;

    return (
      <ul className="events-list">
        {dayEvents.slice(0, 3).map(event => {
          const typeInfo = getEventTypeInfo(event.event_type);
          return (
            <li key={event.id}>
              <Badge
                color={typeInfo.color}
                text={
                  <Tooltip title={`${event.pet_name}: ${event.title}`}>
                    <span className={event.is_completed ? 'completed' : ''}>
                      {event.title.substring(0, 10)}
                      {event.title.length > 10 ? '...' : ''}
                    </span>
                  </Tooltip>
                }
              />
            </li>
          );
        })}
        {dayEvents.length > 3 && (
          <li>
            <Text type="secondary">+{dayEvents.length - 3} {t('petCalendar.more')}</Text>
          </li>
        )}
      </ul>
    );
  };

  const onSelect = (date) => {
    setSelectedDate(date);
  };

  // Check premium status
  if (petStore.premiumLoading) {
    return (
      <div className="pet-calendar-page loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!petStore.premiumStatus?.is_premium) {
    return (
      <div className="pet-calendar-page not-premium">
        <Card>
          <Empty
            image={<CrownOutlined style={{ fontSize: 64, color: '#faad14' }} />}
            description={
              <Space direction="vertical">
                <Title level={4}>{t('petCalendar.premiumFeature.title')}</Title>
                <Text type="secondary">
                  {t('petCalendar.premiumFeature.description')}
                </Text>
              </Space>
            }
          >
            <Button type="primary" onClick={() => navigate('/premium')}>
              {t('petCalendar.premiumFeature.upgradeButton')}
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div className="pet-calendar-page">
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <CalendarOutlined />
                <span>{t('petCalendar.title')}</span>
              </Space>
            }
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateEvent}>
                {t('petCalendar.addEvent')}
              </Button>
            }
          >
            <Calendar
              dateCellRender={dateCellRender}
              onSelect={onSelect}
              onPanelChange={handleMonthChange}
              value={selectedDate}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <BellOutlined />
                <span>{t('petCalendar.upcomingEvents')}</span>
              </Space>
            }
            className="upcoming-events-card"
          >
            {petStore.calendarLoading ? (
              <Spin />
            ) : petStore.upcomingEvents.length === 0 ? (
              <Empty description={t('petCalendar.noUpcomingEvents')} />
            ) : (
              <List
                dataSource={petStore.upcomingEvents}
                renderItem={event => {
                  const typeInfo = getEventTypeInfo(event.event_type);
                  const daysUntil = dayjs(event.event_date).diff(dayjs(), 'day');

                  return (
                    <List.Item
                      actions={[
                        !event.is_completed && (
                          <Tooltip title={t('petCalendar.markComplete')}>
                            <Button
                              type="text"
                              icon={<CheckOutlined />}
                              onClick={() => handleCompleteEvent(event.id)}
                            />
                          </Tooltip>
                        ),
                        <Tooltip title={t('common.edit')}>
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditEvent(event)}
                          />
                        </Tooltip>,
                        <Popconfirm
                          title={t('petCalendar.deleteConfirm')}
                          onConfirm={() => handleDeleteEvent(event.id)}
                          okText={t('common.yes')}
                          cancelText={t('common.no')}
                        >
                          <Tooltip title={t('common.delete')}>
                            <Button type="text" danger icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                      ].filter(Boolean)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Tag color={typeInfo.color}>{t(`petCalendar.eventTypes.${typeInfo.value.toLowerCase()}`)}</Tag>
                        }
                        title={
                          <span className={event.is_completed ? 'completed' : ''}>
                            {event.title}
                          </span>
                        }
                        description={
                          <Space direction="vertical" size={0}>
                            <Text type="secondary">{event.pet_name}</Text>
                            <Text type="secondary">
                              {event.event_date}
                              {event.event_time && ` at ${event.event_time.substring(0, 5)}`}
                            </Text>
                            {daysUntil === 0 && <Tag color="red">{t('petCalendar.today')}</Tag>}
                            {daysUntil === 1 && <Tag color="orange">{t('petCalendar.tomorrow')}</Tag>}
                            {daysUntil > 1 && daysUntil <= 7 && (
                              <Tag color="blue">{t('petCalendar.inDays', { count: daysUntil })}</Tag>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title={editingEvent ? t('petCalendar.modal.editTitle') : t('petCalendar.modal.addTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="pet"
            label={t('petCalendar.modal.pet')}
            rules={[{ required: true, message: t('petCalendar.modal.petRequired') }]}
          >
            <Select placeholder={t('petCalendar.modal.selectPet')}>
              {petStore.myPets.map(pet => (
                <Option key={pet.id} value={pet.id}>
                  {pet.name} ({pet.animal_category_name})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="event_type"
            label={t('petCalendar.modal.eventType')}
            rules={[{ required: true, message: t('petCalendar.modal.eventTypeRequired') }]}
          >
            <Select placeholder={t('petCalendar.modal.selectEventType')}>
              {EVENT_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  <Badge color={type.color} text={t(`petCalendar.eventTypes.${type.value.toLowerCase()}`)} />
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label={t('petCalendar.modal.title')}
            rules={[{ required: true, message: t('petCalendar.modal.titleRequired') }]}
          >
            <Input placeholder={t('petCalendar.modal.titlePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('petCalendar.modal.description')}>
            <TextArea rows={3} placeholder={t('petCalendar.modal.descriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="event_date"
                label={t('petCalendar.modal.date')}
                rules={[{ required: true, message: t('petCalendar.modal.dateRequired') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="event_time" label={t('petCalendar.modal.time')}>
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="recurrence" label={t('petCalendar.modal.repeat')}>
            <Select>
              {RECURRENCE_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {t(`petCalendar.recurrence.${type.value.toLowerCase()}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.recurrence !== curr.recurrence}
          >
            {({ getFieldValue }) =>
              getFieldValue('recurrence') !== 'NONE' && (
                <Form.Item name="recurrence_end_date" label={t('petCalendar.modal.repeatUntil')}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Row gutter={16} align="middle">
            <Col span={12}>
              <Form.Item name="reminder_enabled" label={t('petCalendar.modal.enableReminder')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.reminder_enabled !== curr.reminder_enabled}
              >
                {({ getFieldValue }) =>
                  getFieldValue('reminder_enabled') && (
                    <Form.Item name="reminder_days_before" label={t('petCalendar.modal.daysBefore')}>
                      <InputNumber min={1} max={30} style={{ width: '100%' }} />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalVisible(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={petStore.calendarLoading}>
                {editingEvent ? t('petCalendar.modal.updateButton') : t('petCalendar.modal.createButton')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default PetCalendarPage;
